import { withDependencies } from '@wix/thunderbolt-ioc'
import {
	ComponentsMeasuresAPISymbol,
	FiveGridLineMeasuresSymbol,
	GroupMeasuresSymbol,
	PageChildMeasuresSymbol,
	MediaContainerChildMeasuresSymbol,
} from './symbols'
import { CompRef, IStructureStore, Structure } from '@wix/thunderbolt-symbols'
import type {
	IComponentCustomMeasuresMap,
	IComponentCustomMeasureImperativeAPI,
	IComponentsMeasuresAPI,
	CustomMeasureType,
	IGroupMeasures,
	IFiveGridLineMeasures,
	FiveGridLineMeasureType,
	RegisterComponentMeasuresAPI,
	IPageChildMeasures,
	IMediaContainerChildMeasures,
} from './types'
import { ComponentWillMount, ViewerComponent } from 'feature-components'
import { getFullId } from '@wix/thunderbolt-commons'
import type { SiteMeasureMap } from 'thunderbolt-viewer-manager-types'
import type { IMeasuresApi } from '@wix/thunderbolt-becky-types'

type CustomMeasureComponents = CompRef & IComponentCustomMeasureImperativeAPI

const isComponentWithCustomMeasurement = (ref: CompRef): ref is CustomMeasureComponents =>
	!!ref && 'getCustomMeasures' in ref

const topLeftMeasureTypes = ['left', 'top']

const componentTypes = [
	'Section',
	'QuickActionBar',
	'MatrixGallery',
	'Page',
	'VerticalAnchorsMenu',
	'StateBox',
	'Column',
	'PopupContainer',
	'Group',
	'StripColumnsContainer',
	'ClassicSection',
	'FiveGridLine',
	'AppWidget',
	'WRichText',
	'MediaContainer',
	'RefComponent',
	'MultiStateBox',
	'ToggleSwitch',
]

const registerComponentMeasuresAPIFactory = (
	componentsMeasuresAPI: IComponentsMeasuresAPI,
	structure: IStructureStore,
	{ getMeasuresForFiveGridLine, isFiveGridLineComponent }: IFiveGridLineMeasures
): RegisterComponentMeasuresAPI & ComponentWillMount<ViewerComponent> => {
	const registerComponentCustomMeasure = (ref: CompRef) => {
		if (isComponentWithCustomMeasurement(ref)) {
			const customMeasureGetters = ref.getCustomMeasures()
			Object.entries(customMeasureGetters).forEach(([type, typeMeasures]) => {
				Object.entries(typeMeasures || {}).forEach(([identifier, getter]) => {
					componentsMeasuresAPI.setCustomMeasure(
						type as CustomMeasureType,
						identifier,
						(measureMap, measuresApi) => getter(measureMap, measuresApi)
					)
				})
			})
		}
	}

	const registerFiveGridLineMeasures = (compId: string) =>
		topLeftMeasureTypes.forEach((type) =>
			componentsMeasuresAPI.setCustomMeasure(
				type as CustomMeasureType,
				compId,
				(measureMap, measuresApi) =>
					getMeasuresForFiveGridLine(compId, measureMap, measuresApi!)[type as FiveGridLineMeasureType] // TODO: delete ! when fix EE
			)
		)

	const registerComponentMeasures = (compId: string) => {
		if (isFiveGridLineComponent(compId)) {
			registerFiveGridLineMeasures(compId)
		}
	}

	return {
		componentTypes,
		componentWillMount(viewerComponent) {
			registerComponentMeasures(viewerComponent.id)
		},
		registerComponentCustomMeasures(compId: string, ref: CompRef) {
			const templateCompId = getFullId(compId) // repeater's case: items have inflated ids
			if (!componentTypes.includes(structure.get(templateCompId)?.componentType)) {
				return
			}

			registerComponentCustomMeasure(ref)
		},
	}
}

const componentsMeasuresAPIFactory = (
	{
		getMeasuresForGroupChild,
		getMeasuresForGroup,
		shouldUseCustomMeasureForGroup,
		shouldUseCustomMeasureForGroupChild,
	}: IGroupMeasures,
	{ shouldUseCustomMeasureForPageChild, getMeasuresForPageChild }: IPageChildMeasures,
	{ shouldUseCustomMeasureForMediaContainerChild, getMeasuresForMediaContainerChild }: IMediaContainerChildMeasures
): IComponentsMeasuresAPI => {
	const customMeasures: IComponentCustomMeasuresMap = {
		width: {},
		height: {},
		minHeight: {},
		minWidth: {},
		custom: {},
		relativeToScreenOverrides: {},
		top: {},
		left: {},
		leftOffset: {},
		topOffset: {},
		contentHeight: {},
	}

	const hasCustomMeasure = (identifier: string, type: CustomMeasureType) => {
		return (
			!!customMeasures[type][identifier] ||
			shouldUseCustomMeasureForGroup(identifier, type) ||
			(topLeftMeasureTypes.includes(type) &&
				(shouldUseCustomMeasureForGroupChild(identifier) ||
					shouldUseCustomMeasureForPageChild(identifier) ||
					shouldUseCustomMeasureForMediaContainerChild(identifier)))
		)
	}

	const getCustomMeasure = (
		identifier: string,
		type: CustomMeasureType,
		measureMap: SiteMeasureMap,
		measuresApi: IMeasuresApi
	) => {
		if (shouldUseCustomMeasureForGroupChild(identifier)) {
			return getMeasuresForGroupChild(identifier, measureMap, measuresApi)[
				type as 'left' | 'top' | 'contentHeight'
			]
		}

		if (shouldUseCustomMeasureForGroup(identifier, type)) {
			return getMeasuresForGroup(identifier, measureMap, measuresApi)[type as 'left' | 'top']
		}

		let customMeasure
		if (customMeasures[type][identifier]) {
			customMeasure = customMeasures[type][identifier](measureMap, measuresApi)
		}

		if (shouldUseCustomMeasureForPageChild(identifier)) {
			return getMeasuresForPageChild(identifier, type as 'left' | 'top', measuresApi, measureMap, customMeasure)
		}
		if (shouldUseCustomMeasureForMediaContainerChild(identifier)) {
			return getMeasuresForMediaContainerChild(
				identifier,
				type as 'left' | 'top',
				measuresApi,
				measureMap,
				customMeasure
			)
		}

		return customMeasure
	}

	return {
		hasCustomMeasure,
		tryUsingCustomMeasure: (id, measure, measureMap, measuresApi) => {
			if (hasCustomMeasure(id, measure)) {
				return getCustomMeasure(id, measure, measureMap, measuresApi)
			}
		},
		setCustomMeasure: (type, identifier, measureGetter) => {
			customMeasures[type][identifier] = measureGetter
		},
		isValidMeasure: (measure: any) => Boolean(measure || measure === 0),
	}
}

export const ComponentsMeasuresAPI = withDependencies(
	[GroupMeasuresSymbol, PageChildMeasuresSymbol, MediaContainerChildMeasuresSymbol],
	componentsMeasuresAPIFactory
)

export const RegisterComponentMeasures = withDependencies(
	[ComponentsMeasuresAPISymbol, Structure, FiveGridLineMeasuresSymbol],
	registerComponentMeasuresAPIFactory
)
